import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import DiagnosticQuizContainer from "./DiagnosticQuizContainer";
import DiagnosticResults from "./DiagnosticResults";
import UserInfoForm, { UserInfo } from "../../components/UserInfoForm";
import { diagnosticQuestions, DiagnosticScores } from "./questions";
import { webhookService } from "../../shared/webhookService";

const SESSION_KEY = "diagnostic_quiz_results";

export interface DiagnosticResult {
  questionId: number;
  selectedOptionIndex: number;
  question: string;
  answer: string;
  scores: DiagnosticScores;
}

const DiagnosticQuiz: React.FC = () => {
  const [results, setResults] = useState<DiagnosticResult[]>(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showQuiz, setShowQuiz] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const navigate = useNavigate();

  // Keep sessionStorage in sync whenever results change
  useEffect(() => {
    if (results.length > 0) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(results));
    }
  }, [results]);

  const handleUserInfoSubmit = (userData: UserInfo) => {
    setUserInfo(userData);
    setShowQuiz(true);
  };

  const handleQuizComplete = async (quizResults: DiagnosticResult[]) => {
    setResults(quizResults);

    // Prepare webhook data
    const webhookData = {
      quizName: "The MAP Assessment",
      userInfo: userInfo || {},
      responses: quizResults.map((result, index) => {
        const currentQuestion = diagnosticQuestions.find(
          (question) => question.id === result.questionId,
        );
        const selectedOption =
          currentQuestion?.options[result.selectedOptionIndex];

        return {
          questionNumber: index + 1,
          question: currentQuestion?.question || result.question,
          answer: selectedOption?.text || result.answer,
        };
      }),
      timestamp: new Date().toISOString(),
    };

    // Send webhook before navigation. On failure, webhookService saves locally and retries every hour.
    await webhookService.submitQuizData(webhookData);

    navigate("results", { replace: true });
  };

  const handleRestart = () => {
    setResults([]);
    setShowQuiz(false);
    setUserInfo(null);
    sessionStorage.removeItem(SESSION_KEY);
    navigate(".", { replace: true });
  };

  return (
    <div>
      <Routes>
        <Route
          path="/"
          element={
            showQuiz ? (
              <DiagnosticQuizContainer onQuizComplete={handleQuizComplete} />
            ) : (
              <UserInfoForm
                onSubmit={handleUserInfoSubmit}
                title="The MAP Assessment"
                subtitle="5 questions. Instant results."
                description="Find out exactly where your business is breaking down."
              />
            )
          }
        />
        <Route
          path="results"
          element={<DiagnosticResults results={results} />}
        />
      </Routes>
    </div>
  );
};

export default DiagnosticQuiz;
